//src/services/api.js
import { isLicenseError, parseLicenseError, LicenseError } from '../utils/licenseErrorHandler';

//const API_BASE_URL = process.env.REACT_APP_API_URL || '';
//window._env_ check for environment variables
console.log('API_BASE_URL:', window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL);
console.log('API_BASE_URL:', window._env_);
const API_BASE_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;


class ApiService {
    constructor() {
        this.baseUrl = API_BASE_URL;
    }

    //getBaseUrl
    getBaseUrl() {
        return this.baseUrl;
    }



    // Helper method for making authenticated requests


    async fetchWithAuth(endpoint, options = {}) {
        //console.log('fetchWithAuth', endpoint, options);
        const token = localStorage.getItem('token');
        const url = this.buildUrl(endpoint, options.params);

        try {
            // Don't set Content-Type for FormData to allow browser to set it with boundary
            const headers = options.isFormData
                ? {
                    Authorization: token ? `Bearer ${token}` : '',
                    ...options.headers,
                }
                : {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : '',
                    ...options.headers,
                };

            const response = await fetch(url, {
                ...options,
                headers,
            });

            const data = await response.json();

            // Handle license errors first before checking HTTP status
            if (isLicenseError(data)) {
                const licenseError = parseLicenseError(data);
                console.error(`License Error (${endpoint}):`, licenseError);
                throw licenseError;
            }

            // Handle 401 for non-license errors
            if (response.status === 401) {
                localStorage.removeItem('token');
                window.location.reload();
                throw new Error('Session expired');
            }

            if (!response.ok) {
                throw new Error(data.error || 'Request failed');
            }

            return data;
        } catch (error) {
            // Re-throw license errors as-is
            if (error instanceof LicenseError) {
                throw error;
            }
            
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    /**
     * Make an authenticated request using an API key
     * @param {string} endpoint - API endpoint
     * @param {string} apiKey - API key for authentication
     * @param {Object} options - Fetch options
     * @returns {Promise<any>} - Response data
     */
    async fetchWithApiKey(endpoint, apiKey, options = {}) {
        const url = this.buildUrl(endpoint, options.params);

        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': apiKey,
                    ...options.headers,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Request failed');
            }

            return data;
        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    // Auth endpoints
    async login(username, password) {
        return this.fetchWithAuth('/api/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
    }

    //Validate token
    async validateToken(token) {
        return this.fetchWithAuth('/api/validate-token', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
    }

    async logout() {
        return this.fetchWithAuth('/api/logout', {
            method: 'POST',
        });
    }

    // Conversations endpoints
    async getConversations(params = {}) {
        const queryParams = new URLSearchParams();

        // Add pagination
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);

        // Add search filters (use 'search' parameter instead of 'phone')
        if (params.search) queryParams.append('search', params.search);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);

        // Add cache buster to prevent stale data
        queryParams.append('_t', Date.now());

        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
        return this.fetchWithAuth(`/api/agent/conversations${queryString}`);
    }

    async getChatHistory(phone, page = 1, limit = 50) {
        return this.fetchWithAuth(
            `/api/agent/chat/${phone}?page=${page}&limit=${limit}`
        );
    }



    // Messages endpoints
    async sendMessage(recipientPhone, message, recipientName = '') {
        return this.fetchWithAuth('/api/agent/message', {
            method: 'POST',
            body: JSON.stringify({
                recipientPhone,
                recipientName,
                message,
            }),
        });
    }

    async sendMedia(file, recipientPhone, recipientName = '', caption = '') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('recipientPhone', recipientPhone);
        formData.append('recipientName', recipientName);
        formData.append('caption', caption);
        formData.append('type', this.getFileType(file));

        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${this.baseUrl}/api/agent/media`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    // Don't set Content-Type here, let browser set it with boundary
                },
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to upload media');
            }

            return response.json();
        } catch (error) {
            console.error('Media upload error:', error);
            throw error;
        }
    }

    async markMessagesAsRead(messages) {
        return this.fetchWithAuth('/api/agent/mark-read', {
            method: 'POST',
            body: JSON.stringify({ messages }),
        });
    }

    // Message status endpoints
    async getMessageStatus(messageId) {
        return this.fetchWithAuth(`/api/message-status/${messageId}`);
    }

    async getFailedMessages(timeRange = '24h') {
        return this.fetchWithAuth(`/api/failed-messages?timeRange=${timeRange}`);
    }

    async retryMessage(messageId) {
        return this.fetchWithAuth(`/api/retry-message/${messageId}`, {
            method: 'POST',
        });
    }

    // Helper methods
    getFileType(file) {
        if (file.type.startsWith('image/')) return 'image';
        if (file.type.startsWith('video/')) return 'video';
        if (file.type.startsWith('audio/')) return 'audio';
        return 'document';
    }




    // Dashboard API Methods
    async getReportsSummary(params) {
        return this.fetchWithAuth('/api/reports/summary', {
            method: 'GET',
            params
        });
    }

    async getAnalyticsSentiment(params) {
        return this.fetchWithAuth('/api/analytics/sentiment', {
            method: 'GET',
            params
        });
    }

    async getAnalyticsConversations(params) {
        return this.fetchWithAuth('/api/analytics/conversations', {
            method: 'GET',
            params
        });
    }





    // Metrics and Analytics endpoints
    async getEngagementMetrics(startDate, endDate) {
        return this.fetchWithAuth(
            `/api/analytics/engagement?startDate=${startDate}&endDate=${endDate}`
        );
    }

    async getMessageVolume(startDate, endDate) {
        return this.fetchWithAuth(
            `/api/analytics/messages/volume?startDate=${startDate}&endDate=${endDate}`
        );
    }

    async getUserRetention(startDate, endDate) {
        return this.fetchWithAuth(
            `/api/analytics/users/retention?startDate=${startDate}&endDate=${endDate}`
        );
    }

    async getResponseMetrics(startDate, endDate) {
        return this.fetchWithAuth(
            `/api/analytics/messages/response?startDate=${startDate}&endDate=${endDate}`
        );
    }

    async getActiveUsers(startDate, endDate) {
        return this.fetchWithAuth(
            `/api/analytics/users/active?startDate=${startDate}&endDate=${endDate}`
        );
    }

    async getDailyActivity(startDate, endDate) {
        return this.fetchWithAuth(
            `/api/analytics/activity/daily?startDate=${startDate}&endDate=${endDate}`
        );
    }


    // User Management endpoints
    async getUsers(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.search) queryParams.append('search', params.search);
        if (params.role) queryParams.append('role', params.role);
        //add a query parameter no cache
        queryParams.append('noCache', new Date().getTime());

        return this.fetchWithAuth(`/api/users?${queryParams.toString()}`);
    }


    async createUser(userData) {
        return this.fetchWithAuth('/api/register', {
            method: 'POST',
            body: JSON.stringify(userData)
        });
    }

    async updateUser(userId, userData) {
        return this.fetchWithAuth(`/api/users/${userId}`, {
            method: 'PUT',
            body: JSON.stringify(userData)
        });
    }

    async deleteUser(userId) {
        return this.fetchWithAuth(`/api/users/${userId}`, {
            method: 'DELETE'
        });
    }

    async changeUserPassword(userId, passwords) {
        return this.fetchWithAuth('/api/change-password', {
            method: 'POST',
            body: JSON.stringify(passwords)
        });
    }

    //change password for other users /api/users/:id/change-password newPassword
    async changeOtherUserPassword(userId, newPassword) {
        return this.fetchWithAuth(`/api/users/${userId}/change-password`, {
            method: 'POST',
            body: JSON.stringify(newPassword)
        });
    }




    // Template API Methods
    async getTemplates(filters = {}) {
        return this.fetchWithAuth('/api/templates', {
            method: 'GET',
            params: {
                category: filters.category || '',
                status: filters.status || '',
                language: filters.language || '',
                search: filters.search || '',
                limit: filters.limit || 10,
                offset: filters.offset || 0
            }
        });
    }

    async sendTemplate(payload) {
        return this.fetchWithAuth('/api/send-template', {
            method: 'POST',
            body: JSON.stringify({
                phone: payload.phone || payload.recipientPhone,
                template_name: payload.template_name || payload.templateName,
                language_code: payload.language_code || payload.languageCode,
                components: payload.components
            })
        });
    }

    async createTemplate(templateData) {
        return this.fetchWithAuth('/api/templates', {
            method: 'POST',
            body: JSON.stringify(templateData)
        });
    }

    async uploadTemplateFile(formData) {
        return this.fetchWithAuth('/api/templates/upload-file', {
            method: 'POST',
            body: formData,
            isFormData: true
        });
    }

    async uploadTemplateMedia(data) {
        return this.fetchWithAuth('/api/templates/upload-media', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async updateTemplate(templateId, data) {
        return this.fetchWithAuth(`/api/templates/${templateId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async deleteTemplate(templateId, templateName) {
        return this.fetchWithAuth(`/api/templates/${templateId}`, {
            method: 'DELETE',
            params: {
                name: templateName
            }
        });
    }

    async getTemplateMetadata() {
        return this.fetchWithAuth('/api/templates/metadata/constants');
    }

    async getTemplateById(templateId) {
        return this.fetchWithAuth(`/api/templates/${templateId}`);
    }

    async searchTemplates(filters = {}) {
        return this.fetchWithAuth('/api/templates/search', {
            method: 'GET',
            params: {
                category: filters.category || '',
                name: filters.name || '',
                language: filters.language || '',
                status: filters.status || '',
                limit: filters.limit || 20,
                offset: filters.offset || 0
            }
        });
    }

    async getTemplateMessageMetrics(startDate, endDate, interval = 'daily') {
        return this.fetchWithAuth(`/api/analytics/templates`, {
            method: 'GET',
            params: {
                startDate,
                endDate,
                interval
            }
        });
    }

    async getTemplateMessages(startDate, endDate, options = {}) {
        const {
            page = 1,
            limit = 10,
            templateName = '',
            status = '',
            recipient = '',
            sortBy = 'timestamp',
            sortOrder = 'desc'
        } = options;

        return this.fetchWithAuth(`/api/analytics/templates/messages`, {
            method: 'GET',
            params: {
                startDate,
                endDate,
                page,
                limit,
                templateName,
                status,
                recipient,
                sortBy,
                sortOrder
            }
        });
    }

    // Save Flow API Methods
    async saveFlow(reactFlowData) {
        try {
            // Format the flow data
            const flowData = {
                name: reactFlowData.name || 'New Flow', // Add a name field in your UI
                description: reactFlowData.description || '',
                nodes: reactFlowData.nodes.map(node => ({
                    id: node.id,
                    type: node.type,
                    position: {
                        x: node.position.x,
                        y: node.position.y
                    },
                    data: {
                        ...node.data,
                        // Clean up any UI-specific properties
                        selected: undefined,
                        dragging: undefined
                    },
                    width: node.width,
                    height: node.height,
                    deletable: node.deletable
                })),
                edges: reactFlowData.edges.map(edge => ({
                    id: edge.id,
                    source: edge.source,
                    sourceHandle: edge.sourceHandle,
                    target: edge.target,
                    targetHandle: edge.targetHandle,
                    type: edge.type || 'smoothstep',
                    animated: edge.animated || true,
                    style: edge.style || { stroke: '#888' },
                    label: edge.label || ''
                })),
                // Add any additional metadata
                metadata: {
                    lastModified: new Date().toISOString(),
                    version: reactFlowData.version || 1,
                    status: 'draft'
                }
            };

            return await this.fetchWithAuth(`/api/flows`, {
                method: 'POST',
                body: JSON.stringify(flowData)
            });
        } catch (error) {
            console.error('Error saving flow:', error);
            throw error;
        }
    }


    async updateFlow(flowId, reactFlowData) {
        try {
            const flowData = {
                ...reactFlowData,
                metadata: {
                    lastModified: new Date().toISOString(),
                    version: (reactFlowData.version || 1) + 1
                }
            };

            return await this.fetchWithAuth(`/api/flows/${flowId}`, {
                method: 'PUT',
                body: JSON.stringify(flowData)
            });
        } catch (error) {
            console.error('Error updating flow:', error);
            throw error;
        }
    }

    async getFlow(flowId) {
        try {
            return await this.fetchWithAuth(`/api/flows/${flowId}`);
        } catch (error) {
            console.error('Error getting flow:', error);
            throw error;
        }
    }

    async validateFlow(flowData) {
        // Basic flow validation
        if (!flowData.nodes || !flowData.edges) {
            throw new Error('Invalid flow data: missing nodes or edges');
        }

        // Validate start node exists
        const startNode = flowData.nodes.find(node => node.type === 'start');
        if (!startNode) {
            throw new Error('Flow must have a start node');
        }

        // Validate node connections
        const nodeIds = new Set(flowData.nodes.map(node => node.id));
        for (const edge of flowData.edges) {
            if (!nodeIds.has(edge.source)) {
                throw new Error(`Invalid edge: source node ${edge.source} not found`);
            }
            if (!nodeIds.has(edge.target)) {
                throw new Error(`Invalid edge: target node ${edge.target} not found`);
            }
        }

        // Validate node data
        for (const node of flowData.nodes) {
            switch (node.type) {
                case 'message':
                    if (!node.data?.message) {
                        throw new Error(`Message node ${node.id} must have a message`);
                    }
                    break;
                case 'condition':
                    if (!node.data?.variable || !node.data?.operator) {
                        throw new Error(`Condition node ${node.id} must have a variable and operator`);
                    }
                    break;
                case 'quickReply':
                    if (!node.data?.question || !node.data?.options?.length) {
                        throw new Error(`Quick reply node ${node.id} must have a question and options`);
                    }
                    break;
                case 'action':
                    if (!node.data?.actionType) {
                        throw new Error(`Action node ${node.id} must have an action type`);
                    }
                    break;
                default:
                    throw new Error(`Unknown node type: ${node.type}`);


            }
        }

        return true;
    }
    // In api.js, add these methods:

    async getFlows(filters = {}) {
        const queryParams = new URLSearchParams();

        // Add pagination parameters
        queryParams.append('page', filters.page || 1);
        queryParams.append('limit', filters.limit || 10);

        // Add search if provided
        if (filters.search) {
            queryParams.append('search', filters.search);
        }

        // Add status filter if provided
        if (filters.status === 'active') {
            queryParams.append('isActive', 'true');
        } else if (filters.status === 'inactive') {
            queryParams.append('isActive', 'false');
        }

        // Add no-cache parameter to prevent caching
        queryParams.append('noCache', new Date().getTime());

        return this.fetchWithAuth(`/api/listflows?${queryParams.toString()}`);
    }

    async deleteFlow(flowId) {
        return this.fetchWithAuth(`/api/flows/${flowId}`, {
            method: 'DELETE'
        });
    }

    // start flow 
    async startFlow({ flowId, userId, metadata = {} }) {
        return this.fetchWithAuth('/api/sessions/start', {
            method: 'POST',
            body: JSON.stringify({
                flowId,
                userId,
                metadata
            })
        });
    }

    async continueFlow(sessionId, { input }) {
        return this.fetchWithAuth(`/api/sessions/${sessionId}/continue`, {
            method: 'POST',
            body: JSON.stringify({ input })
        });
    }

    async endSession(sessionId, { reason }) {
        return this.fetchWithAuth(`/api/sessions/${sessionId}/end`, {
            method: 'POST',
            body: JSON.stringify({ reason })
        });
    }

    async getSessionState(sessionId) {
        return this.fetchWithAuth(`/api/sessions/${sessionId}/state`, {
            method: 'GET'
        });
    }


    ///api/logout-all - POST - Logout all Sessions for a user

    // Helper method to handle URL parameters
    buildUrl(endpoint, params) {
        const url = new URL(`${this.baseUrl}${endpoint}`);
        //console.log('url', url);
        if (params) {
            Object.keys(params).forEach(key =>
                url.searchParams.append(key, params[key])
            );
        }
        return url.toString();
    }

    // Session Management endpoints
    async getSessionStatus(phone) {
        return this.fetchWithAuth(`/api/agent/session/${phone}`);
    }

    async takeoverSession(phone, reason = 'agent_takeover') {
        return this.fetchWithAuth(`/api/agent/session/${phone}/takeover`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        });
    }

    async returnToBot(phone) {
        return this.fetchWithAuth(`/api/agent/session/${phone}/return-to-bot`, {
            method: 'POST',
        });
    }

    async endSession(phone, reason = 'agent_ended', sendMessage = true, message = '') {
        return this.fetchWithAuth(`/api/agent/session/${phone}/end`, {
            method: 'POST',
            body: JSON.stringify({
                reason,
                sendMessage,
                message
            }),
        });
    }

    // API Key Management endpoints
    async createApiKey(keyData) {
        return this.fetchWithAuth('/api/keys', {
            method: 'POST',
            body: JSON.stringify(keyData),
        });
    }

    async getApiKeys() {
        return this.fetchWithAuth('/api/keys');
    }

    async getApiKey(keyId) {
        return this.fetchWithAuth(`/api/keys/${keyId}`);
    }

    async updateApiKey(keyId, keyData) {
        return this.fetchWithAuth(`/api/keys/${keyId}`, {
            method: 'PUT',
            body: JSON.stringify(keyData),
        });
    }

    async revokeApiKey(keyId) {
        return this.fetchWithAuth(`/api/keys/${keyId}`, {
            method: 'DELETE',
        });
    }

    async getApiKeyLogs(keyId, page = 1, limit = 20) {
        return this.fetchWithAuth(`/api/keys/${keyId}/logs?page=${page}&limit=${limit}`);
    }

    // Messaging endpoints with API key support
    async sendTextWithApiKey(apiKey, phone, message) {
        return this.fetchWithApiKey('/api/send-text', apiKey, {
            method: 'POST',
            body: JSON.stringify({
                phone,
                message,
            }),
        });
    }

    async sendTemplateWithApiKey(apiKey, payload) {
        return this.fetchWithApiKey('/api/send-template', apiKey, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async sendMediaWithApiKey(apiKey, file, phone, caption = '') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('phone', phone);
        formData.append('caption', caption);
        formData.append('type', this.getFileType(file));

        try {
            const response = await fetch(`${this.baseUrl}/api/send-media`, {
                method: 'POST',
                headers: {
                    'X-API-Key': apiKey,
                    // Don't set Content-Type here, let browser set it with boundary
                },
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to send media');
            }

            return response.json();
        } catch (error) {
            console.error('Media send error:', error);
            throw error;
        }
    }

    async sendButtonsWithApiKey(apiKey, payload) {
        return this.fetchWithApiKey('/api/send-buttons', apiKey, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    // Special fetch method for license endpoints that handles setup-phase 401s gracefully
    async fetchLicenseAuth(endpoint, options = {}) {
        const token = localStorage.getItem('token');
        const url = this.buildUrl(endpoint, options.params);

        try {
            // Don't set Content-Type for FormData to allow browser to set it with boundary
            const headers = options.isFormData
                ? {
                    Authorization: token ? `Bearer ${token}` : '',
                    ...options.headers,
                }
                : {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : '',
                    ...options.headers,
                };

            const response = await fetch(url, {
                ...options,
                headers,
            });

            const data = await response.json();

            // Handle license errors first
            if (isLicenseError(data)) {
                const licenseError = parseLicenseError(data);
                console.error(`License Error (${endpoint}):`, licenseError);
                throw licenseError;
            }

            // Handle license endpoint 401s more gracefully - might be setup issue
            if (response.status === 401) {
                throw new Error('License endpoints require authentication or are not properly configured');
            }

            // Handle 404 for license endpoints
            if (response.status === 404) {
                throw new Error('License endpoint not found. Please ensure license management is implemented on the backend');
            }

            if (!response.ok) {
                throw new Error(data.error || 'License request failed');
            }

            return data;
        } catch (error) {
            // Re-throw license errors as-is
            if (error instanceof LicenseError) {
                throw error;
            }
            
            console.error(`License API Error (${endpoint}):`, error);
            throw error;
        }
    }

    // License Management endpoints
    async getLicenseDetails() {
        return this.fetchLicenseAuth('/api/license/details');
    }

    async updateLicense(licenseKey, hmacSecret = '') {
        return this.fetchLicenseAuth('/api/license/update', {
            method: 'POST',
            body: JSON.stringify({
                licenseKey,
                hmacSecret: hmacSecret || undefined
            })
        });
    }

    async revokeLicense() {
        return this.fetchLicenseAuth('/api/license/revoke', {
            method: 'DELETE'
        });
    }

    async getLicenseStatus() {
        return this.fetchLicenseAuth('/api/license/status');
    }

    async reloadLicenseFromEnv() {
        return this.fetchLicenseAuth('/api/license/reload-env', {
            method: 'POST'
        });
    }

    // Storage Management endpoints
    async getStorageDetails(refresh = false) {
        return this.fetchLicenseAuth('/api/license/storage', {
            params: refresh ? { refresh: 'true' } : {}
        });
    }

    async clearStorageCache() {
        return this.fetchLicenseAuth('/api/license/storage/cache', {
            method: 'DELETE'
        });
    }

    async cleanupStorage(options = {}) {
        return this.fetchLicenseAuth('/api/storage/cleanup', {
            method: 'POST',
            body: JSON.stringify({
                days: options.days || 30,
                dryRun: options.dryRun || false,
                directories: options.directories || ['media_storage', 'uploads']
            })
        });
    }

    async getCleanupStatus(directories = []) {
        const params = directories.length > 0 ? { directories: directories.join(',') } : {};
        return this.fetchLicenseAuth('/api/storage/cleanup/status', {
            params
        });
    }

    async previewCleanup(days = 30, directories = []) {
        const params = { days: days.toString() };
        if (directories.length > 0) {
            params.directories = directories.join(',');
        }
        return this.fetchLicenseAuth('/api/storage/cleanup/preview', {
            params
        });
    }

    async stopCleanup() {
        return this.fetchLicenseAuth('/api/storage/cleanup/stop', {
            method: 'POST'
        });
    }

    // Database Management endpoints
    async getDatabaseStatus(collections = []) {
        const params = collections.length > 0 ? { collections: collections.join(',') } : {};
        return this.fetchLicenseAuth('/api/database/cleanup/status', {
            params
        });
    }

    async cleanupDatabase(options = {}) {
        return this.fetchLicenseAuth('/api/database/cleanup', {
            method: 'POST',
            body: JSON.stringify({
                days: options.days || 30,
                dryRun: options.dryRun || false,
                collections: options.collections || ['messages', 'activities', 'usageEvents'],
                criteria: options.criteria || {},
                archiveBeforeDelete: options.archiveBeforeDelete || false,
                batchSize: options.batchSize || 1000
            })
        });
    }

    async previewDatabaseCleanup(days = 30, collections = []) {
        const params = { days: days.toString() };
        if (collections.length > 0) {
            params.collections = collections.join(',');
        }
        return this.fetchLicenseAuth('/api/database/cleanup/preview', {
            params
        });
    }

    async stopDatabaseCleanup() {
        return this.fetchLicenseAuth('/api/database/cleanup/stop', {
            method: 'POST'
        });
    }

    async optimizeDatabase(operations = ['reIndex', 'stats'], collections = []) {
        return this.fetchLicenseAuth('/api/database/optimize', {
            method: 'POST',
            body: JSON.stringify({
                operations,
                collections
            })
        });
    }

    // Email Management endpoints
    async getEmailStatus() {
        return this.fetchWithAuth('/api/email/status');
    }

    async getEmailSettings() {
        return this.fetchWithAuth('/api/email/settings');
    }

    async updateEmailSettings(settings) {
        return this.fetchWithAuth('/api/email/settings', {
            method: 'PUT',
            body: JSON.stringify({ settings })
        });
    }

    async testEmailConfiguration() {
        return this.fetchWithAuth('/api/email/test', {
            method: 'POST'
        });
    }

    async getEmailTemplates() {
        return this.fetchWithAuth('/api/email/templates');
    }

    async updateEmailTemplate(templateKey, template) {
        return this.fetchWithAuth(`/api/email/templates/${templateKey}`, {
            method: 'PUT',
            body: JSON.stringify({ template })
        });
    }

    async checkEmailVerificationRequired() {
        return this.fetchWithAuth('/api/email/verification-required');
    }

    // Auth Password Reset endpoints
    async forgotPassword(email) {
        return this.fetchWithAuth('/api/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    }

    async resetPassword(token, newPassword) {
        return this.fetchWithAuth('/api/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, newPassword })
        });
    }

    // Auth Email Verification endpoints
    async verifyEmail(token) {
        return this.fetchWithAuth('/api/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token })
        });
    }

    async resendVerification(email) {
        return this.fetchWithAuth('/api/auth/resend-verification', {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    }

    // App Settings endpoints
    async getAppSettings() {
        return this.fetchWithAuth('/api/app/settings');
    }

    async getPublicAppSettings() {
        const url = this.buildUrl('/api/app/public');
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Request failed');
            }
            
            return data;
        } catch (error) {
            console.error('API Error (/api/app/public):', error);
            throw error;
        }
    }

    async updateAppSettings(settings) {
        return this.fetchWithAuth('/api/app/settings', {
            method: 'PUT',
            body: JSON.stringify({ settings })
        });
    }

}

// Export both the class and the instance
export { ApiService };
export const apiService = new ApiService();